<script>
	import { tick } from 'svelte';
	import { queryConceptTree, emailQueryAnswer } from '../lib/api.js';
	import Navbar from '../components/Navbar.svelte';

	export let navigate;

	let queryInput = '';
	let queryResult = null;
	let queryLoading = false;
	let queryError = null;
	let queryEmailing = false;
	let queryEmailed = false;

	// Ref to the response lane so we can auto-scroll to it on mobile after
	// an answer arrives — on narrow viewports the response sits below the
	// input lane and otherwise has no visual cue that it populated.
	let responseLaneEl;

	async function submitQuery() {
		if (!queryInput.trim() || queryLoading) return;
		queryLoading = true;
		queryResult = null;
		queryError = null;
		queryEmailed = false;
		try {
			queryResult = await queryConceptTree(queryInput);
		} catch (err) {
			queryError = err.message;
		}
		queryLoading = false;

		// On mobile: once the answer renders, slide it into view.
		// On desktop the two lanes are side-by-side, no scroll needed.
		if (typeof window !== 'undefined' && window.innerWidth <= 768 && (queryResult || queryError)) {
			await tick();
			if (responseLaneEl && typeof responseLaneEl.scrollIntoView === 'function') {
				responseLaneEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
			}
		}
	}

	async function emailQueryResult() {
		if (queryEmailing || !queryResult) return;
		queryEmailing = true;
		try {
			const sources = (queryResult.sources || []).map(s => ({
				label: s.label || null,
				title: s.display_name || s.source_filename,
				doc_reference: s.display_name || s.source_filename,
				relevance: s.concepts?.[0]?.similarity ? Math.round(s.concepts[0].similarity * 100) : null,
				sections: (s.concepts || []).map(c => ({ heading: c.concept }))
			}));
			await emailQueryAnswer(queryInput, queryResult.answer, sources, queryResult.timing_ms);
			queryEmailed = true;
		} catch { /* silent */ }
		queryEmailing = false;
	}

	function handleQueryKeydown(e) {
		if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submitQuery(); }
	}

	function resetQuery() {
		queryInput = '';
		queryResult = null;
		queryError = null;
		queryEmailed = false;
	}

	function renderMarkdown(text) {
		if (!text) return '';
		let html = text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
		html = html.replace(/```(\w*)\n([\s\S]*?)```/g, (_, lang, code) => `<pre class="qmd-pre"><code>${code.trim()}</code></pre>`);
		html = html.replace(/`([^`]+)`/g, '<code class="qmd-code">$1</code>');
		html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
		html = html.replace(/\*(.+?)\*/g, '<em>$1</em>');
		html = html.replace(/^### (.+)$/gm, '<h4 class="qmd-h4">$1</h4>');
		html = html.replace(/^## (.+)$/gm, '<h3 class="qmd-h3">$1</h3>');
		html = html.replace(/^# (.+)$/gm, '<h2 class="qmd-h2">$1</h2>');
		html = html.replace(/(?:^[\-\*] .+$\n?)+/gm, match => {
			const items = match.trim().split('\n').map(l => `<li>${l.replace(/^[\-\*] /, '')}</li>`).join('');
			return `<ul class="qmd-list">${items}</ul>`;
		});
		html = html.replace(/\n\n+/g, '</p><p>');
		html = `<p>${html}</p>`;
		html = html.replace(/<p>\s*<\/p>/g, '');

		// Inline citations: [A], [AB] — style as subtle reference pills so the
		// eye parses them as pointers, not noise. Legacy comma-separated
		// variants ([A, B]) get split into adjacent pills for consistent UI.
		html = html.replace(/\[([A-Z]{1,2}(?:\s*,\s*[A-Z]{1,2})+)\]/g, (_m, inner) => {
			return inner.split(/\s*,\s*/)
				.map(label => `<span class="qmd-cite">[${label}]</span>`)
				.join('');
		});
		html = html.replace(/\[([A-Z]{1,2})\]/g, '<span class="qmd-cite">[$1]</span>');
		return html;
	}

	// Labeled sources for the References section below the answer.
	$: labeledSources = (queryResult?.sources || []).filter(s => s && s.label);
</script>

<svelte:head><title>Query - ADRASI</title></svelte:head>

<div class="q-page">
<Navbar active="query" {navigate} on:refresh={resetQuery} />

<div class="q-columns">
	<div class="q-lane q-input-lane">
		<div class="q-lane-header">Query</div>
		<div class="q-input-body">
			<textarea
				class="q-textarea"
				placeholder="Ask a question about your concepts..."
				bind:value={queryInput}
				on:keydown={handleQueryKeydown}
				rows="4"
				disabled={queryLoading}
			></textarea>
			<button class="q-submit" on:click={submitQuery} disabled={!queryInput.trim() || queryLoading}>
				{queryLoading ? 'Asking...' : 'Ask'}
			</button>
		</div>
		<div class="q-sources-section">
			<div class="q-sources-label">{queryResult ? 'Sources' : 'Pro tip: Email questions anytime'}</div>
			<div class="q-lane-scroll">
				{#if queryResult?.sources?.length}
					{#each queryResult.sources as source (source.display_name || source.source_filename)}
						<div class="q-source-card">
							<div class="q-source-head">
								{#if source.label}<span class="q-source-label">[{source.label}]</span>{/if}
								<span class="q-source-name">{source.display_name || source.source_filename}</span>
								{#if source.domain}<span class="q-source-domain">{source.domain}</span>{/if}
							</div>
							{#if source.concepts?.length}
								<div class="q-source-concepts">
									{#each source.concepts as c}
										<span class="q-concept-pill" title="similarity {Math.round(c.similarity * 100)}%">{c.concept}</span>
									{/each}
								</div>
							{/if}
						</div>
					{/each}
				{:else if queryResult}
					<div class="q-lane-empty">No sources</div>
				{:else}
					<div class="q-protip">
						<p class="q-protip-body">Send any question to <span class="q-protip-email">ask@adrasi.com</span> and receive a cited answer straight to your inbox.</p>
					</div>
				{/if}
			</div>
		</div>
	</div>

	<div class="q-lane q-response-lane" bind:this={responseLaneEl}>
		<div class="q-lane-header">Response</div>
		<div class="q-lane-scroll q-response-body">
			{#if queryLoading}
				<div class="q-state q-pulse">Fetching your answer,<br>please standby.</div>
			{:else if queryError}
				<div class="q-state q-error">{queryError}</div>
			{:else if queryResult}
				<div class="q-answer">{@html renderMarkdown(queryResult.answer)}</div>
				{#if labeledSources.length}
					<div class="q-references">
						<div class="q-references-label">References</div>
						<ol class="q-references-list">
							{#each labeledSources as s (s.label)}
								<li class="q-reference-item">
									<span class="q-reference-key">[{s.label}]</span>
									<span class="q-reference-name">{s.display_name || s.source_filename}</span>
									{#if s.domain}<span class="q-reference-domain">{s.domain}</span>{/if}
								</li>
							{/each}
						</ol>
					</div>
				{/if}
			{:else}
				<div class="q-state">Answer will appear here</div>
			{/if}
		</div>
		{#if queryResult}
			<div class="q-email-strip">
				<button class="q-email" on:click={emailQueryResult} disabled={queryEmailing || queryEmailed}>
					{queryEmailed ? 'Sent' : queryEmailing ? 'Sending...' : 'Email me'}
				</button>
			</div>
		{/if}
	</div>
</div>

</div>

<style>
	.q-page { height: 100vh; display: flex; flex-direction: column; overflow: hidden; background: #E8EAEE; color: #1A1A1A; font-family: -apple-system, BlinkMacSystemFont, 'Inter', 'Segoe UI', sans-serif; }

	.q-columns { display: grid; grid-template-columns: 1fr 2fr; gap: 0; background: #F1F3F7; flex: 1; min-height: 0; overflow: hidden; border-top: 1px solid #D8DBE2; }
	.q-lane { display: flex; flex-direction: column; min-height: 0; overflow: hidden; border-right: 1px dashed #C4C9D4; background: #F5F6FA; }
	.q-lane:last-child { border-right: none; }
	.q-input-lane { background: #ECEEF3; }
	.q-lane-header { height: 40px; box-sizing: border-box; display: flex; align-items: center; padding: 0 16px; font-size: 10px; font-weight: 600; color: #4B5563; text-transform: uppercase; letter-spacing: 0.06em; border-bottom: 1px solid #D8DBE2; flex-shrink: 0; }
	.q-lane-scroll { flex: 1; overflow-y: auto; scrollbar-gutter: stable; }
	.q-lane-empty { padding: 32px 16px; font-size: 12px; color: #C5C8CD; text-align: center; }

	.q-input-body { padding: 16px; display: flex; flex-direction: column; gap: 10px; }
	.q-textarea { width: 100%; box-sizing: border-box; background: #F5F6FA; border: 1px solid #C4C9D4; border-radius: 6px; padding: 10px 12px; font-size: 13px; font-family: inherit; color: #1A1A1A; line-height: 1.55; resize: none; outline: none; transition: border-color 120ms; }
	.q-textarea::placeholder { color: #4B5563; }
	.q-textarea:focus { border-color: #9CA3AF; }
	.q-submit { align-self: flex-end; font-size: 12px; font-weight: 600; color: #111827; background: #FFFFFF; border: 1px solid #C4C9D4; border-radius: 5px; padding: 5px 18px; cursor: pointer; transition: all 120ms ease-out; }
	.q-submit:hover:not(:disabled) { background: #F5F6FA; border-color: #9CA3AF; }
	.q-submit:disabled { opacity: 0.4; cursor: default; }

	.q-sources-section { flex: 1; display: flex; flex-direction: column; min-height: 0; border-top: 1px solid #D8DBE2; }
	.q-sources-label { height: 32px; box-sizing: border-box; display: flex; align-items: center; padding: 0 14px; font-size: 10px; font-weight: 600; letter-spacing: 0.06em; text-transform: uppercase; color: #4B5563; flex-shrink: 0; border-bottom: 1px solid #D8DBE2; }

	.q-protip { padding: 20px 16px; }
	.q-protip-body { margin: 0; font-size: 12px; line-height: 1.6; color: #4B5563; }
	.q-protip-email { color: #2563EB; font-style: normal; }

	.q-source-card { padding: 10px 14px; border-bottom: 1px solid #E4E7ED; }
	.q-source-card:hover { background: #ECEEF3; }
	.q-source-head { display: flex; align-items: baseline; gap: 8px; margin-bottom: 4px; }
	.q-source-name { font-size: 12px; font-weight: 600; color: #111827; word-break: break-all; flex: 1; min-width: 0; }
	.q-source-domain { flex-shrink: 0; font-size: 9px; font-weight: 500; letter-spacing: 0.04em; text-transform: uppercase; color: #6B7280; }
	.q-source-concepts { display: flex; flex-wrap: wrap; gap: 4px; margin-top: 2px; }
	.q-concept-pill { font-size: 10px; color: #4B5563; background: #E4E7ED; border-radius: 3px; padding: 2px 6px; line-height: 1.4; }

	.q-response-body { padding: 16px; }
	.q-state { padding: 48px 24px; font-size: 13px; color: #C5C8CD; text-align: center; line-height: 1.7; }
	@keyframes q-pulse-fade { 0%, 100% { opacity: 1; } 50% { opacity: 0.2; } }
	.q-pulse { font-size: 14px; font-weight: 500; color: #4B5563; animation: q-pulse-fade 2s ease-in-out infinite; }
	.q-error { color: #B45309; }

	.q-answer { font-size: 13px; color: #374151; line-height: 1.65; }
	.q-answer :global(p) { margin: 0 0 10px; }
	.q-answer :global(strong) { font-weight: 600; }
	.q-answer :global(.qmd-h2) { font-size: 15px; font-weight: 600; color: #111827; margin: 14px 0 6px; }
	.q-answer :global(.qmd-h3) { font-size: 13px; font-weight: 600; color: #111827; margin: 12px 0 4px; }
	.q-answer :global(.qmd-list) { margin: 6px 0 10px 16px; padding: 0; }
	.q-answer :global(.qmd-pre) { background: #ECEEF3; border-radius: 5px; padding: 10px 12px; font-size: 12px; overflow-x: auto; margin: 8px 0; }
	.q-answer :global(.qmd-code) { background: #ECEEF3; border-radius: 3px; padding: 1px 5px; font-size: 12px; }

	/* Inline citation — reads exactly as body text. The [A] marker is the
	   whole visual cue; no pill or color differentiation needed. */
	.q-answer :global(.qmd-cite) {
		font-variant-numeric: tabular-nums;
	}

	/* References section — rendered below the answer, keyed to inline [X] tokens. */
	.q-references {
		margin-top: 22px;
		padding-top: 14px;
		border-top: 1px solid #D8DBE2;
	}
	.q-references-label {
		font-size: 10px;
		font-weight: 600;
		letter-spacing: 0.06em;
		text-transform: uppercase;
		color: #4B5563;
		margin-bottom: 8px;
	}
	.q-references-list { list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; gap: 4px; }
	.q-reference-item {
		display: flex;
		align-items: baseline;
		gap: 8px;
		font-size: 12px;
		color: #374151;
		line-height: 1.5;
		flex-wrap: wrap;
	}
	.q-reference-key {
		font-weight: 600;
		color: #1F2937;
		background: #DDE1EA;
		border-radius: 3px;
		padding: 0 6px;
		font-size: 11px;
		font-variant-numeric: tabular-nums;
		flex-shrink: 0;
	}
	.q-reference-name { color: #111827; font-weight: 500; word-break: break-word; }
	.q-reference-domain {
		font-size: 9px;
		font-weight: 500;
		letter-spacing: 0.04em;
		text-transform: uppercase;
		color: #6B7280;
	}

	/* Label on the left-lane source cards — mirrors the inline citation pill
	   so the eye can jump between answer and sources without thinking. */
	.q-source-label {
		font-size: 10px;
		font-weight: 600;
		color: #1F2937;
		background: #DDE1EA;
		border-radius: 3px;
		padding: 0 5px;
		flex-shrink: 0;
		font-variant-numeric: tabular-nums;
	}

	.q-email-strip { flex-shrink: 0; display: flex; align-items: center; justify-content: flex-end; padding: 10px 16px; border-top: 1px solid #E8E8ED; }
	.q-email { font-size: 11px; font-weight: 500; color: #2563EB; background: none; border: 1px solid #2563EB; border-radius: 5px; padding: 4px 14px; cursor: pointer; transition: all 120ms ease-out; }
	.q-email:hover:not(:disabled) { color: #1D4ED8; border-color: #1D4ED8; }
	.q-email:disabled { opacity: 0.5; cursor: default; }

	@media (max-width: 768px) {
		/* Release the fixed-viewport-height lock so stacked lanes can
		   grow naturally and the page scrolls like any normal page.
		   Each lane still has its own internal scroll for sources/answer
		   when content is long. */
		.q-page { height: auto; min-height: 100vh; overflow: visible; }
		.q-columns {
			grid-template-columns: 1fr;
			overflow: visible;
		}
		.q-lane {
			overflow: visible;
			border-right: none;
			border-bottom: 1px solid #D8DBE2;
		}
		.q-lane:last-child { border-bottom: none; }
		.q-input-body { padding: 14px; }
		.q-textarea { font-size: 14px; padding: 12px; }
		.q-submit {
			align-self: stretch;
			padding: 12px 18px;
			font-size: 13px;
			min-height: 44px;
		}
		.q-response-body { padding: 14px; }
		.q-answer { font-size: 14px; line-height: 1.6; }
		.q-lane-scroll { max-height: none; }
		/* Scroll margin so the scrollIntoView target doesn't sit flush under
		   the sticky navbar when the auto-scroll fires. */
		.q-response-lane { scroll-margin-top: 60px; }
	}
</style>
